import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  SwatchIcon,
  BellIcon,
  EyeIcon,
  Cog6ToothIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogContentLarge } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePreferencesUIStore } from '@/store/preferencesUIStore';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';
import {
  ACCENT_COLOR_OPTIONS,
  type AccentColor,
  type DensityMode,
  type ThemeMode,
} from '@/types/appearance';
import {
  parseHexColor,
  parseRgbString,
  contrastRatio,
  WCAG_AA_NORMAL,
} from '@/utils/contrastRatio';

const MAIN_CATEGORIES = [
  { id: 'general', label: 'General', icon: Cog6ToothIcon },
  { id: 'appearance', label: 'Appearance', icon: SwatchIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'accessibility', label: 'Accessibility', icon: EyeIcon },
  { id: 'matters', label: 'Engagements', icon: BriefcaseIcon },
  { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
  { id: 'clients', label: 'Clients', icon: UsersIcon },
  { id: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon },
  { id: 'calendar', label: 'Calendar', icon: CalendarDaysIcon },
] as const;

type MainCategory = typeof MAIN_CATEGORIES[number]['id'];

const THEME_MODE_OPTIONS: { id: ThemeMode; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'Use system settings' },
];

const DENSITY_OPTIONS: { id: DensityMode; label: string; description: string }[] = [
  { id: 'compact', label: 'Compact', description: 'Fit more content on screen' },
  { id: 'cozy', label: 'Cozy', description: 'Balanced spacing for daily work' },
  { id: 'comfortable', label: 'Comfortable', description: 'Extra breathing room' },
];

const APP_DENSITY_OPTIONS = [
  { id: 'inherit', label: 'Inherit global density', description: 'Use the global density setting.' },
  ...DENSITY_OPTIONS,
] as const;

const MATTER_VIEW_OPTIONS = [
  { id: 'table', label: 'Table view' },
  { id: 'card', label: 'Card view' },
  { id: 'kanban', label: 'Kanban view' },
] as const;

const DOCUMENT_VIEW_OPTIONS = [
  { id: 'table', label: 'Table view' },
  { id: 'grid', label: 'Grid view' },
] as const;

const DOCUMENT_SORT_OPTIONS = [
  { id: 'updated_at', label: 'Recently updated' },
  { id: 'created_at', label: 'Recently created' },
  { id: 'title', label: 'Title' },
  { id: 'size', label: 'File size' },
  { id: 'type', label: 'File type' },
] as const;

const TASK_SORT_OPTIONS = [
  { id: 'due_date', label: 'Due date' },
  { id: 'priority', label: 'Priority' },
  { id: 'created_at', label: 'Created date' },
] as const;

const TASK_STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'review', label: 'In review' },
  { id: 'done', label: 'Done' },
  { id: 'archived', label: 'Archived' },
] as const;

const CALENDAR_VIEW_OPTIONS = [
  { id: 'month', label: 'Month view' },
  { id: 'week', label: 'Week view' },
  { id: 'workWeek', label: 'Work week view' },
  { id: 'day', label: 'Day view' },
  { id: 'agenda', label: 'Agenda view' },
] as const;

const WEEK_START_OPTIONS = [
  { id: 'system', label: 'System default' },
  { id: 'sunday', label: 'Sunday' },
  { id: 'monday', label: 'Monday' },
] as const;

export default function PreferencesModal() {
  const { isOpen, close } = usePreferencesUIStore();
  const [activeCategory, setActiveCategory] = useState<MainCategory>('appearance');

  useEffect(() => {
    if (!isOpen) return;
    setActiveCategory('appearance');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && close()}>
      <DialogContentLarge className="lex-text-override-escape bg-[var(--brand-surface-card)] border border-[var(--brand-border)] shadow-2xl overflow-hidden">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--brand-border)]">
            <h1 className="text-xl font-semibold text-[var(--brand-text-primary)]">Preferences</h1>

            <div className="flex-1 max-w-xs mx-8">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--brand-text-tertiary)]" />
                <Input
                  type="text"
                  placeholder="Search settings"
                  className="w-full pl-9 pr-4 py-2 text-sm rounded-lg"
                />
              </div>
            </div>

            <button
              onClick={close}
              className="p-2 rounded-lg text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)] transition-colors"
              aria-label="Close preferences"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <aside className="w-56 border-r border-[var(--brand-border)] bg-[var(--brand-surface-sidebar)] p-3 space-y-1">
              {MAIN_CATEGORIES.map((category) => {
                const isActive = category.id === activeCategory;
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-primary)]'
                        : 'text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)]'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {category.label}
                  </button>
                );
              })}
            </aside>

            <section className="flex-1 overflow-y-auto">
              {activeCategory === 'appearance' ? (
                <AppearancePanel />
              ) : activeCategory === 'general' ? (
                <PlaceholderPanel
                  title="General"
                  description="General preferences for WeBelieve will live here."
                />
              ) : activeCategory === 'notifications' ? (
                <PlaceholderPanel
                  title="Notifications"
                  description="Notification preferences will be centralized here."
                />
              ) : activeCategory === 'accessibility' ? (
                <PlaceholderPanel
                  title="Accessibility"
                  description="Accessibility controls will be added here."
                />
              ) : activeCategory === 'matters' ? (
                <EngagementsPanel />
              ) : activeCategory === 'documents' ? (
                <DocumentsPanel />
              ) : activeCategory === 'clients' ? (
                <ClientsPanel />
              ) : activeCategory === 'tasks' ? (
                <TasksPanel />
              ) : activeCategory === 'calendar' ? (
                <CalendarPanel />
              ) : (
                <PlaceholderPanel
                  title={activeCategory}
                  description={`App-specific preferences for ${activeCategory} will be added here.`}
                />
              )}
            </section>
          </div>
        </div>
      </DialogContentLarge>
    </Dialog>
  );
}

function PlaceholderPanel({ title, description }: { title: string; description: string }) {
  const displayTitle = title.charAt(0).toUpperCase() + title.slice(1);
  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-[var(--brand-text-primary)]">{displayTitle}</h2>
      <p className="mt-2 text-sm text-[var(--brand-text-tertiary)]">{description}</p>
    </div>
  );
}

function AppearancePanel() {
  const { appearancePreferences, setAppearancePreferences } = useUserPreferencesStore();
  const {
    themeMode,
    accentColor,
    accentCustomHex,
    density,
    fontScale,
    contrastMode,
    fontCustomColor,
    fontBodyColor,
  } = appearancePreferences;
  const fontDefaultColor = appearancePreferences.fontDefaultColor ?? null;
  const fontDefaultBodyColor = appearancePreferences.fontDefaultBodyColor ?? null;
  const fontCustomColorForce = appearancePreferences.fontCustomColorForce ?? false;
  const fontBodyColorForce = appearancePreferences.fontBodyColorForce ?? false;

  const accentOptions = useMemo(() => ACCENT_COLOR_OPTIONS, []);
  const customAccentValue = accentCustomHex || '#3b82f6';

  return (
    <div className="p-6 max-w-4xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-[var(--brand-text-primary)]">Appearance</h2>
        <p className="text-sm text-[var(--brand-text-tertiary)] mt-1">
          Customize the WeBelieve interface for your workflow.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Theme mode</h3>
        <div className="flex flex-wrap gap-3">
          {THEME_MODE_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => setAppearancePreferences({ themeMode: option.id })}
              className={`min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors ${
                themeMode === option.id
                  ? 'border-[var(--brand-accent-primary)] bg-[var(--brand-accent-primary-soft)]'
                  : 'border-[var(--brand-border)] hover:bg-[var(--brand-surface-hover)]'
              }`}
            >
              <p className="text-sm font-medium text-[var(--brand-text-primary)]">{option.label}</p>
              <p className="text-xs text-[var(--brand-text-tertiary)] mt-1">Applies instantly</p>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Accent color</h3>
        <div className="flex flex-wrap gap-2">
          {accentOptions.map((color) => (
            <AccentSwatch
              key={color.id}
              id={color.id}
              label={color.label}
              colorValue={color.id === 'custom' ? customAccentValue : color.colorValue}
              selected={accentColor === color.id}
              onSelect={(id) => {
                setAppearancePreferences({ accentColor: id });
                if (id === 'custom' && !accentCustomHex) {
                  setAppearancePreferences({ accentCustomHex: customAccentValue });
                }
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-[var(--brand-text-tertiary)]">Custom color</label>
          <input
            type="color"
            value={customAccentValue}
            onChange={(event) => {
              setAppearancePreferences({ accentColor: 'custom', accentCustomHex: event.target.value });
            }}
            className="h-8 w-12 rounded-md border border-[var(--brand-border)] bg-[var(--brand-surface-card)]"
          />
          <span className="text-xs text-[var(--brand-text-tertiary)]">{customAccentValue.toUpperCase()}</span>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Text colors</h3>
        <p className="text-xs text-[var(--brand-text-tertiary)] max-w-xl">
          Override the system-wide text color per category. Headings drive the
          dashboard titles, agent names, and section headers. Body text drives
          descriptions, helper text, and labels — tertiary and muted shades are
          derived as faded versions of the body color to preserve hierarchy. If a
          choice fails the WCAG AA contrast check it stays saved but isn&apos;t
          applied — use <span className="font-medium">Apply anyway</span> on that
          row to force it. This modal always remains readable regardless of your
          selection.
        </p>

        <FontColorRow
          label="Headings"
          themeMode={themeMode}
          defaultLight={THEME_TEXT_DEFAULT_LIGHT}
          defaultDark={THEME_TEXT_DEFAULT_DARK}
          value={fontCustomColor}
          forceApplied={fontCustomColorForce}
          onChange={(next) =>
            setAppearancePreferences({ fontCustomColor: next, fontCustomColorForce: false })
          }
          onApplyAnyway={() => setAppearancePreferences({ fontCustomColorForce: true })}
        />

        <FontColorRow
          label="Body text"
          themeMode={themeMode}
          defaultLight={THEME_BODY_DEFAULT_LIGHT}
          defaultDark={THEME_BODY_DEFAULT_DARK}
          value={fontBodyColor}
          forceApplied={fontBodyColorForce}
          onChange={(next) =>
            setAppearancePreferences({ fontBodyColor: next, fontBodyColorForce: false })
          }
          onApplyAnyway={() => setAppearancePreferences({ fontBodyColorForce: true })}
        />

        <TextColorActionsRow
          fontCustomColor={fontCustomColor ?? null}
          fontBodyColor={fontBodyColor ?? null}
          fontDefaultColor={fontDefaultColor}
          fontDefaultBodyColor={fontDefaultBodyColor}
          forceHeading={fontCustomColorForce}
          forceBody={fontBodyColorForce}
          onSetDefault={() =>
            setAppearancePreferences({
              fontDefaultColor: fontCustomColor ?? null,
              fontDefaultBodyColor: fontBodyColor ?? null,
            })
          }
          onReset={() =>
            setAppearancePreferences({
              fontCustomColor: fontDefaultColor,
              fontBodyColor: fontDefaultBodyColor,
              fontCustomColorForce: false,
              fontBodyColorForce: false,
            })
          }
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Density</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {DENSITY_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => setAppearancePreferences({ density: option.id })}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                density === option.id
                  ? 'border-[var(--brand-accent-primary)] bg-[var(--brand-accent-primary-soft)]'
                  : 'border-[var(--brand-border)] hover:bg-[var(--brand-surface-hover)]'
              }`}
            >
              <p className="text-sm font-medium text-[var(--brand-text-primary)]">{option.label}</p>
              <p className="text-xs text-[var(--brand-text-tertiary)] mt-1">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Text scale</h3>
        <RangeControl
          label="Font size"
          value={fontScale}
          min={0.9}
          max={1.2}
          step={0.05}
          display={`${Math.round(fontScale * 100)}%`}
          onChange={(value) => setAppearancePreferences({ fontScale: value })}
        />
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">Contrast mode</h3>
        <div className="flex flex-wrap gap-3">
          {['standard', 'high'].map((mode) => (
            <button
              key={mode}
              onClick={() => setAppearancePreferences({ contrastMode: mode as 'standard' | 'high' })}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                contrastMode === mode
                  ? 'border-[var(--brand-accent-primary)] bg-[var(--brand-accent-primary-soft)]'
                  : 'border-[var(--brand-border)] hover:bg-[var(--brand-surface-hover)]'
              }`}
            >
              <p className="text-sm font-medium text-[var(--brand-text-primary)]">
                {mode === 'high' ? 'High contrast' : 'Standard'}
              </p>
              <p className="text-xs text-[var(--brand-text-tertiary)] mt-1">
                {mode === 'high' ? 'Sharper edges & stronger text' : 'Default appearance'}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Theme defaults match the .lex-text-override-escape rule in brand-theme.css —
// the modal always renders these regardless of the user's override, so these
// are the colors the pickers show when no override is set.
const THEME_TEXT_DEFAULT_LIGHT = '#0f172a';
const THEME_TEXT_DEFAULT_DARK = '#f8fafc';
const THEME_BODY_DEFAULT_LIGHT = '#475569';
const THEME_BODY_DEFAULT_DARK = '#cbd5e1';

const isThemeDark = (mode: ThemeMode): boolean =>
  mode === 'dark' ||
  (mode === 'system' &&
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches);

/**
 * One row of the Text colors section: a single picker (color + hex + contrast
 * badge + Apply-anyway). Both Headings and Body text use this component.
 * Set-as-default and Reset live in the parent (TextColorActionsRow) because
 * they act on both rows together.
 */
function FontColorRow({
  label,
  themeMode,
  defaultLight,
  defaultDark,
  value,
  forceApplied,
  onChange,
  onApplyAnyway,
}: {
  label: string;
  themeMode: ThemeMode;
  defaultLight: string;
  defaultDark: string;
  value: string | null | undefined;
  forceApplied: boolean;
  onChange: (next: string | null) => void;
  onApplyAnyway: () => void;
}) {
  const themeDefaultHex = isThemeDark(themeMode) ? defaultDark : defaultLight;
  const currentHex = value || themeDefaultHex;
  const isOverride = value != null;

  // Read the live app-surface background for the contrast check. We sample
  // <body> rather than the modal because the override applies system-wide,
  // not just inside the (escape-zone-protected) modal.
  const [bgColor, setBgColor] = useState<string>('');
  useEffect(() => {
    setBgColor(getComputedStyle(document.body).backgroundColor);
  }, [themeMode, value]);

  const fgRgb = parseHexColor(currentHex);
  const bgRgb = parseRgbString(bgColor);
  const ratio = fgRgb && bgRgb ? contrastRatio(fgRgb, bgRgb) : null;
  const passes = ratio !== null && ratio >= WCAG_AA_NORMAL;
  const showApplyAnyway = isOverride && ratio !== null && !passes && !forceApplied;

  const commitHex = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (parseHexColor(normalized)) onChange(normalized);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="w-24 text-xs font-medium text-[var(--brand-text-primary)]">{label}</span>
      <input
        type="color"
        value={currentHex}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-12 rounded-md border border-[var(--brand-border)] bg-[var(--brand-surface-card)]"
        aria-label={`${label} color`}
      />
      <input
        type="text"
        value={currentHex.toUpperCase()}
        onChange={(event) => commitHex(event.target.value)}
        className="w-24 rounded-md border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-2 py-1 text-xs text-[var(--brand-text-primary)]"
        aria-label={`${label} hex value`}
      />
      {ratio !== null && (
        <span
          className={`text-xs font-medium ${
            passes || forceApplied
              ? 'text-[var(--brand-status-success)]'
              : 'text-[var(--brand-status-danger)]'
          }`}
        >
          {ratio.toFixed(2)}:1 ·{' '}
          {passes
            ? 'passes AA'
            : forceApplied
            ? 'forced (low contrast)'
            : 'low contrast — not applied'}
        </span>
      )}
      {showApplyAnyway && (
        <button
          type="button"
          onClick={onApplyAnyway}
          className="ml-auto rounded-md border border-[var(--brand-status-warning)] bg-[var(--brand-status-warning-bg)] px-3 py-1 text-xs font-medium text-[var(--brand-text-primary)] hover:opacity-90"
          title="Apply this low-contrast color despite the WCAG warning"
        >
          Apply anyway
        </button>
      )}
    </div>
  );
}

/**
 * Shared actions row for the Text colors section: Set as default + Reset.
 * Both buttons act on heading + body together (option (a) global semantics).
 */
function TextColorActionsRow({
  fontCustomColor,
  fontBodyColor,
  fontDefaultColor,
  fontDefaultBodyColor,
  forceHeading,
  forceBody,
  onSetDefault,
  onReset,
}: {
  fontCustomColor: string | null;
  fontBodyColor: string | null;
  fontDefaultColor: string | null;
  fontDefaultBodyColor: string | null;
  forceHeading: boolean;
  forceBody: boolean;
  onSetDefault: () => void;
  onReset: () => void;
}) {
  const hasOverride = fontCustomColor != null || fontBodyColor != null;
  const hasSavedDefault = fontDefaultColor != null || fontDefaultBodyColor != null;
  const currentMatchesDefault =
    fontCustomColor === fontDefaultColor && fontBodyColor === fontDefaultBodyColor;
  const setDefaultDisabled = !hasOverride || currentMatchesDefault;
  const resetDisabled = currentMatchesDefault && !forceHeading && !forceBody;
  const resetLabel = hasSavedDefault ? 'Reset to my defaults' : 'Reset to defaults';

  return (
    <div className="space-y-2">
      {hasSavedDefault && (
        <p className="text-xs text-[var(--brand-text-tertiary)]">
          Your saved defaults:{' '}
          <span className="font-mono text-[var(--brand-text-primary)]">
            heading {(fontDefaultColor ?? '—').toUpperCase()}
          </span>{' '}
          ·{' '}
          <span className="font-mono text-[var(--brand-text-primary)]">
            body {(fontDefaultBodyColor ?? '—').toUpperCase()}
          </span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onSetDefault}
          disabled={setDefaultDisabled}
          className="rounded-md border border-[var(--brand-border)] px-3 py-1 text-xs text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          title="Save the current heading + body colors as your personal defaults. Reset will return here."
        >
          Set as default
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={resetDisabled}
          className="rounded-md border border-[var(--brand-border)] px-3 py-1 text-xs text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resetLabel}
        </button>
      </div>
    </div>
  );
}

function EngagementsPanel() {
  const { appPreferences, setAppPreferences } = useUserPreferencesStore();
  const prefs = appPreferences.matters;

  return (
    <PanelContainer
      title="Engagements"
      description="Set default layouts and density for engagement workspaces."
    >
      <PanelSection title="Default view">
        <SelectField
          value={prefs.defaultView}
          options={MATTER_VIEW_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ matters: { defaultView: value as typeof prefs.defaultView } })
          }
        />
      </PanelSection>
      <PanelSection title="Density">
        <SelectField
          value={prefs.density}
          options={APP_DENSITY_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ matters: { density: value as typeof prefs.density } })
          }
        />
      </PanelSection>
      <ToggleField
        label="Show KPI summary"
        description="Display the summary tiles above the engagement list."
        checked={prefs.showKpiSummary}
        onChange={(checked) => setAppPreferences({ matters: { showKpiSummary: checked } })}
      />
    </PanelContainer>
  );
}

function DocumentsPanel() {
  const { appPreferences, setAppPreferences } = useUserPreferencesStore();
  const prefs = appPreferences.documents;

  return (
    <PanelContainer
      title="Documents"
      description="Choose defaults for the document library."
    >
      <PanelSection title="Default view">
        <SelectField
          value={prefs.defaultView}
          options={DOCUMENT_VIEW_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ documents: { defaultView: value as typeof prefs.defaultView } })
          }
        />
      </PanelSection>
      <PanelSection title="Default sort">
        <SelectField
          value={prefs.defaultSort}
          options={DOCUMENT_SORT_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ documents: { defaultSort: value as typeof prefs.defaultSort } })
          }
        />
      </PanelSection>
      <PanelSection title="Density">
        <SelectField
          value={prefs.density}
          options={APP_DENSITY_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ documents: { density: value as typeof prefs.density } })
          }
        />
      </PanelSection>
      <ToggleField
        label="Show analytics"
        description="Keep the analytics tab visible in the documents workspace."
        checked={prefs.showAnalytics}
        onChange={(checked) => setAppPreferences({ documents: { showAnalytics: checked } })}
      />
    </PanelContainer>
  );
}

function ClientsPanel() {
  const { appPreferences, setAppPreferences } = useUserPreferencesStore();
  const prefs = appPreferences.clients;

  return (
    <PanelContainer
      title="Clients"
      description="Control the default view for client lists and dashboards."
    >
      <PanelSection title="Default view">
        <SelectField
          value={prefs.defaultView}
          options={MATTER_VIEW_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ clients: { defaultView: value as typeof prefs.defaultView } })
          }
        />
      </PanelSection>
      <PanelSection title="Density">
        <SelectField
          value={prefs.density}
          options={APP_DENSITY_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ clients: { density: value as typeof prefs.density } })
          }
        />
      </PanelSection>
      <ToggleField
        label="Show KPI summary"
        description="Display summary tiles at the top of the clients dashboard."
        checked={prefs.showKpiSummary}
        onChange={(checked) => setAppPreferences({ clients: { showKpiSummary: checked } })}
      />
    </PanelContainer>
  );
}

function TasksPanel() {
  const { appPreferences, setAppPreferences } = useUserPreferencesStore();
  const prefs = appPreferences.tasks;

  return (
    <PanelContainer
      title="Tasks"
      description="Set defaults for task lists and prioritization."
    >
      <PanelSection title="Default status filter">
        <SelectField
          value={prefs.defaultStatusFilter}
          options={TASK_STATUS_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ tasks: { defaultStatusFilter: value as typeof prefs.defaultStatusFilter } })
          }
        />
      </PanelSection>
      <PanelSection title="Default sort">
        <SelectField
          value={prefs.defaultSort}
          options={TASK_SORT_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ tasks: { defaultSort: value as typeof prefs.defaultSort } })
          }
        />
      </PanelSection>
      <PanelSection title="Density">
        <SelectField
          value={prefs.density}
          options={APP_DENSITY_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ tasks: { density: value as typeof prefs.density } })
          }
        />
      </PanelSection>
      <ToggleField
        label="Highlight overdue tasks"
        description="Emphasize tasks that have passed their due date."
        checked={prefs.showOverdueHighlight}
        onChange={(checked) => setAppPreferences({ tasks: { showOverdueHighlight: checked } })}
      />
    </PanelContainer>
  );
}

function CalendarPanel() {
  const { appPreferences, setAppPreferences } = useUserPreferencesStore();
  const prefs = appPreferences.calendar;

  return (
    <PanelContainer
      title="Calendar"
      description="Tune the calendar defaults for your schedule view."
    >
      <PanelSection title="Default view">
        <SelectField
          value={prefs.defaultView}
          options={CALENDAR_VIEW_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ calendar: { defaultView: value as typeof prefs.defaultView } })
          }
        />
      </PanelSection>
      <PanelSection title="Week start">
        <SelectField
          value={prefs.weekStart}
          options={WEEK_START_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ calendar: { weekStart: value as typeof prefs.weekStart } })
          }
        />
      </PanelSection>
      <PanelSection title="Working hours">
        <SelectField
          value={prefs.workingHoursMode}
          options={[
            { id: 'system', label: 'Use subscriber defaults' },
            { id: 'custom', label: 'Custom hours' },
          ]}
          onChange={(value) =>
            setAppPreferences({ calendar: { workingHoursMode: value as typeof prefs.workingHoursMode } })
          }
        />
        {prefs.workingHoursMode === 'custom' && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              type="time"
              value={prefs.workingHoursStart}
              onChange={(event) =>
                setAppPreferences({ calendar: { workingHoursStart: event.target.value } })
              }
            />
            <Input
              type="time"
              value={prefs.workingHoursEnd}
              onChange={(event) =>
                setAppPreferences({ calendar: { workingHoursEnd: event.target.value } })
              }
            />
          </div>
        )}
      </PanelSection>
      <PanelSection title="Density">
        <SelectField
          value={prefs.density}
          options={APP_DENSITY_OPTIONS}
          onChange={(value) =>
            setAppPreferences({ calendar: { density: value as typeof prefs.density } })
          }
        />
      </PanelSection>
      <ToggleField
        label="Show weekends"
        description="Include Saturdays and Sundays in calendar views."
        checked={prefs.showWeekends}
        onChange={(checked) => setAppPreferences({ calendar: { showWeekends: checked } })}
      />
    </PanelContainer>
  );
}

function AccentSwatch({
  id,
  label,
  colorValue,
  selected,
  onSelect,
}: {
  id: AccentColor;
  label: string;
  colorValue: string;
  selected: boolean;
  onSelect: (id: AccentColor) => void;
}) {
  return (
    <button
      onClick={() => onSelect(id)}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${
        selected
          ? 'border-[var(--brand-accent-primary)] bg-[var(--brand-accent-primary-soft)]'
          : 'border-[var(--brand-border)] hover:bg-[var(--brand-surface-hover)]'
      }`}
      title={label}
    >
      <span
        className="h-4 w-4 rounded-full border border-white/40"
        style={{ backgroundColor: colorValue }}
      />
      <span className="text-[var(--brand-text-secondary)]">{label}</span>
    </button>
  );
}

function PanelContainer({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="p-6 max-w-4xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[var(--brand-text-primary)]">{title}</h2>
        <p className="text-sm text-[var(--brand-text-tertiary)] mt-1">{description}</p>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function PanelSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-[var(--brand-text-primary)]">{title}</h3>
      {children}
    </div>
  );
}

function SelectField({
  value,
  options,
  onChange,
}: {
  value: string;
  options: ReadonlyArray<{ id: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-focus-ring)] focus:border-[var(--brand-accent-primary)]"
    >
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function ToggleField({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-4 py-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-[var(--brand-accent-primary)]"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <span className="block text-sm font-medium text-[var(--brand-text-primary)]">{label}</span>
        <span className="block text-xs text-[var(--brand-text-tertiary)] mt-1">{description}</span>
      </span>
    </label>
  );
}

function RangeControl({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[var(--brand-text-secondary)]">{label}</span>
        <span className="text-xs text-[var(--brand-text-tertiary)]">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full accent-[var(--brand-accent-primary)]"
      />
    </div>
  );
}
